"use client";

// THE RELAY BAR — the capsule that takes over once the bar at the top of the page has scrolled away.
//
// One component for the whole site. Until 22/09 this envelope was written inside components/home-v2/nav.tsx and
// existed on the homepage alone: the annex pages had only their top bar, so scrolling down there left the reader
// with no menu at all, and it never came back (Jérémy, 22/09: « le menu dans la page About doit être similaire à
// celui esthétiquement de la homepage : centré, et fixed quand on scroll »).
// It is shared, not copied. components/site-nav.css was born as a copy of components/home-v2/nav.css and the two
// drifted far enough that Jérémy had to report the SAME defect twice (the lead item's box, 19/09). A third copy of
// the capsule would have been the same story a third time.
//
// What this file owns: the glass, its gradient edge, its arrival on scroll, the 44 px zone of the logo — everything
// in components/fixed-bar.css. What it does NOT own: what goes inside. The homepage dresses it with the four hero
// anchors and its section-following pill (home-v2/nav.tsx, FixedNav); the annex pages dress it with their five
// links and the page they are on (site-fixed-nav.tsx). Those lists are genuinely different — the homepage's anchors
// (#why, #northos…) do not exist on /about — so the LIST is exactly the thing that stays with each caller.
//
// The trigger is a DOM element passed by the caller, not a scroll position: the capsule appears once that element
// has gone entirely above the viewport. The homepage watches the hero card its top bar lives in; the annex pages
// watch the top bar itself. Both are "the bar you were reading has left", which is the only thing this bar means.

import { useEffect, useRef, useState, type ReactNode, type RefObject } from "react";
import "@/components/ds/button.css"; // .ds-edge: the glass stroke of the pill
import "@/components/home-v2/nav.css"; // the row both bars wear
import "./fixed-bar.css";

/** True once the element `find` returns has scrolled entirely above the viewport. */
function useAnchorGone(bar: RefObject<HTMLDivElement | null>, find: (bar: HTMLElement) => Element | null | undefined) {
  const [gone, setGone] = useState(false);
  // Held in a ref, not read from the dependency list: callers pass an inline arrow, and a new identity on every
  // render would tear the observer down and build it again for nothing.
  const findRef = useRef(find);
  findRef.current = find;

  useEffect(() => {
    const anchor = bar.current ? findRef.current(bar.current) : null;
    if (!anchor || !("IntersectionObserver" in window)) return;
    const io = new IntersectionObserver(([entry]) => {
      if (entry) setGone(!entry.isIntersecting && entry.boundingClientRect.bottom <= 0);
    });
    io.observe(anchor);
    return () => io.disconnect();
  }, [bar]);

  return gone;
}

export default function FixedBar({
  anchor,
  logo,
  variant,
  barRef,
  children,
}: {
  /** The element to watch, found from the bar itself: the capsule shows once it is above the viewport. */
  anchor: (bar: HTMLElement) => Element | null | undefined;
  /** The whole mark link — its destination differs (an anchor to the top here, the home page there). */
  logo: ReactNode;
  /** An extra class for the callers that need one. The homepage passes none, and its HTML is unchanged. */
  variant?: string;
  /** For a caller that needs the bar element for something else (the homepage feeds the hero its scroll). */
  barRef?: RefObject<HTMLDivElement | null>;
  children: (shown: boolean) => ReactNode;
}) {
  const own = useRef<HTMLDivElement>(null);
  const ref = barRef ?? own;
  const gone = useAnchorGone(ref, anchor);

  return (
    <div
      ref={ref}
      className={variant ? `v2-fixednav ${variant}` : "v2-fixednav"}
      data-shown={gone ? "" : undefined}
      inert={!gone}
    >
      <div className="v2-fixednav__inner">
        {logo}
        {children(gone)}
      </div>
    </div>
  );
}
